/**
 * MemberType for a group
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import sanitize from 'sanitize-filename';
import { Alert, Button } from 'react-bootstrap';
import { EMPLOYEE_ROLE, PARENT_ROLE, STUDENT_ROLE, GROUPTYPES_CLASS } from 'containers/User/constants.js';
import usePagination from 'components/Pagination/usePagination.jsx';
import Spinner from 'components/Spinner/index.jsx';
import Search from 'components/interaction/Search/index.jsx';
import SelectUsers from 'components/interaction/SelectUsers/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import IconButton from 'components/interaction/IconButton/index.jsx';
import CheckMark from 'components/media/Icon2/CheckMark.jsx';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import useUpdateAvatar from 'components/user/UploadAvatar/useUpdateAvatar.js';
import UploadModal from 'components/user/UploadAvatar/UploadModal.jsx';
import { idValues } from 'utils/arrays.js';
import { getIdNumber } from 'utils/userIdRole.js';
import { useWidth } from 'utils/checkMedia.js';
import messages from './messages.js';
import ExportUsers from '../../../Users/ExportUsers.jsx';
import selectOptions from './selectOptions.js';
import { MEMBERTYPE_ADMINGROUP, MEMBER_USERS_BY_ID } from '../../constants.js';
import getPathBase from '../../../getPathBase.js';
import AdminTable from '../../../AdminTable.js';
import InviteStudentMembers from './InviteStudentMembers/index.jsx';
import UserControl from './UserControl.jsx';
import ResetPasswordModal from '../../../Users/User/ResetPasswordModal.jsx';
import InviteParentModal from '../../../Users/User/InviteParentModal.jsx';
import useMemberType from './useMemberType.js';

const PAGESIZE = 10;

const Wrapper = styled.div`
  header {
    padding: 15px 0;
  }
  .table-responsive {
    overflow-y: auto;
  }
`;

function MemberType(props) {
  const { group, memberType, readOnly, search, setSearch } = props;
  const { members, handleAdd, handleAddAll, handleDelete, getUploadAvatarOptions } = useMemberType(group, memberType, search);
  const { id: groupId, groupType } = group;
  const responsive = !useWidth('sm');
  const showInviteStudents = memberType.id === STUDENT_ROLE && groupType === GROUPTYPES_CLASS;
  const [show, setShow] = useState(false);
  const [updateAvatar, setUpdateAvatar] = useState(null);
  const [inviteParentUser, setInviteParentUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const isAdmin = getPathBase() === 'admin';
  const showControl = !isAdmin && memberType.id === STUDENT_ROLE; // Allow controls for group managers
  const handleChange = useUpdateAvatar(getUploadAvatarOptions(updateAvatar?.id));

  const memberRoleName = MEMBER_USERS_BY_ID[memberType.id];
  const intl = useIntl();
  const { slice, Paginator, setPage } = usePagination(members?.totalCount, PAGESIZE, members.loadMore);
  if (!members || members.error) {
    return null;
  }

  const memberList = idValues(members);
  const memberTypeString = intl.formatMessage(messages[memberType.name]).toLowerCase();
  const prompt = `${intl.formatMessage(messages.select)} ${memberTypeString}`;
  const exclusions = [...memberList.map(getIdNumber), getIdNumber(group)];

  function getUserLink(user) {
    return (isAdmin && memberRoleName) ? `/admin/users/${memberRoleName}/${user.id}` : undefined;
  }

  function handleSearch(value) {
    setSearch(value);
    setPage(0);
  }

  const filename = `parents-${sanitize(group.name)}.csv`;
  function control(users) {
    const confirmText = intl.formatMessage(messages.confirmAddAll, { count: users.totalCount, type: memberTypeString });
    return (
      <Button variant="light" onClick={() => handleAddAll(confirmText)}>
        <FormattedMessage {...messages.addAll} />
      </Button>
    );
  }
  const canAddAll = memberType.id === EMPLOYEE_ROLE;
  const membersSlice = slice(idValues(members));
  const showMembership = members.list?.some((id) => members[id].dependencies?.length > 0);
  const isAdminGroup = memberType.id === MEMBERTYPE_ADMINGROUP;

  return (
    <Wrapper>
      {showInviteStudents && <InviteStudentMembers students={members} className="mt-4 mb-2 border" />}
      <header className="d-flex flex-wrap">
        <div className="flex-grow-1">
          <Search value={search} onChange={handleSearch} />
        </div>
        {memberType.id === PARENT_ROLE && (
          <ExportUsers filename={filename} roleType={memberType.id} groupId={groupId} className="me-sm-3 input-height" />
        )}
        {!readOnly && <IconButton icon="ss-plus lower-1" variant="primary" onClick={() => setShow(true)}><FormattedMessage {...messages.add} /></IconButton>}
      </header>
      {members.totalCount === 0 ? (
        <Alert variant="info" className="m-4"><FormattedMessage {...messages.noMembers} /></Alert>
      ) : (
        <AdminTable responsive={responsive} size="sm">
          <thead>
            <tr>
              <th className="name w-50"><FormattedMessage {...messages.name} /></th>
              {showInviteStudents && (
                <>
                  <th className="w-25"><FormattedMessage {...messages.active} /></th>
                  <th className="w-25"><FormattedMessage {...messages.parent} /></th>
                </>
              )}
              {isAdminGroup && (
                <>
                  <th className="w-25"><FormattedMessage {...messages.grade} /></th>
                  <th className="w-25"><FormattedMessage {...messages.studentCount} /></th>
                </>
              )}
              {showMembership && <th className="w-25 text-start"><FormattedMessage {...messages.membership} /></th>}
              <th className="ps-3">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {membersSlice.map((member, idx) => (
              <tr key={member.id}>
                <td>
                  <UserAvatar user={member} group={!member.roleType} className="flex-grow-1" link={getUserLink(member)} />
                </td>
                {showInviteStudents && (
                  <>
                    <td><CheckMark value={Boolean(member.uniqueId)} /></td>
                    <td>{member.connectionCount || ''}</td>
                  </>
                )}
                {isAdminGroup && (
                  <>
                    <td>{member.yearClass}</td>
                    <td>{member.studentCount}</td>
                  </>
                )}

                {showMembership && (
                  <td className="text-start">
                    {member.dependencies.length > 0 && (
                      <span className="small text-muted">{member.dependencies.map((o) => o.name).join(', ')}</span>
                    )}
                  </td>
                )}
                <td className="text-end">
                  {showControl ? (
                    <UserControl
                      user={member}
                      dropup={responsive && idx >= Math.max(3, membersSlice.length / 2)}
                      onDelete={handleDelete}
                      setUpdateAvatar={setUpdateAvatar}
                      setInviteParentUser={setInviteParentUser}
                      setResetPasswordUser={setResetPasswordUser}
                    />
                  ) : (
                    <DeleteButton
                      disabled={!member.canDelete}
                      disabledIcon="ss-lock fs-3"
                      className="py-0"
                      onClick={() => handleDelete(member)}
                      confirmText={{ ...messages.confirmRemoveMember, values: member }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </AdminTable>
      )}
      {members.loading && <Spinner />}
      <Paginator />
      <SelectUsers
        show={show}
        {...selectOptions(memberType)}
        value={memberList}
        exclusions={exclusions}
        onChange={handleAdd}
        onHide={() => setShow(false)}
        prompt={prompt}
        control={canAddAll ? control : undefined}
      />
      {updateAvatar && (
        <UploadModal show user={updateAvatar} onHide={() => setUpdateAvatar(null)} onChange={handleChange} />
      )}
      <ResetPasswordModal user={resetPasswordUser} onHide={() => setResetPasswordUser(null)} />
      <InviteParentModal user={inviteParentUser} onHide={() => setInviteParentUser(null)} />
    </Wrapper>
  );
}

MemberType.propTypes = {
  group: PropTypes.object.isRequired,
  memberType: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  search: PropTypes.string,
  setSearch: PropTypes.func,
};

export default MemberType;
